import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useSeo, Reveal } from "../components/site/Seo";
import { ProductCard, SectionHeader } from "../components/site/Cards";
import { TestInterfaceMockup, AnalyticsMockup } from "../components/site/Mockups";
import { examIcon } from "../components/site/icons";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import {
  ArrowRight, Play, Check, ChevronRight, MonitorPlay, LineChart, FileClock,
  Languages, RefreshCw, BarChart4, Layers, ListChecks, Timer,
} from "lucide-react";

const CAPABILITIES = ["Full-length mocks", "Previous-year questions", "Bilingual practice", "Detailed solutions", "Performance analytics"];

const VALUES = [
  { icon: MonitorPlay, title: "Real exam experience", desc: "Practice in a structured interface built around competitive-exam workflows — palette, timer, sections and all." },
  { icon: LineChart, title: "Detailed analysis", desc: "See accuracy, time usage and subject-level performance after every attempt, not just a total score." },
  { icon: FileClock, title: "Previous-year questions", desc: "Practise questions from earlier examinations, organised by exam and subject where appropriate." },
  { icon: Languages, title: "Bilingual practice", desc: "Attempt tests in English or Hindi wherever the specific product provides both languages." },
  { icon: RefreshCw, title: "Smart review", desc: "Revisit your incorrect and marked questions with explanations to close knowledge gaps." },
  { icon: BarChart4, title: "Progress over time", desc: "Track how your score and accuracy move across multiple attempts and full test series." },
];

const AUDIENCE = [
  { t: "First-time aspirants", d: "Build exam temperament with realistic, full-length practice from day one." },
  { t: "Working professionals", d: "Prepare around a busy schedule with focused mocks and clear analytics." },
  { t: "Hindi-medium students", d: "Practise in Hindi wherever the product offers bilingual content." },
  { t: "Repeat attempters", d: "Fix the specific weak areas that held back your previous attempt." },
];

const STEPS = [
  { n: "01", t: "Pick your exam & series", d: "Choose the test series built around your target exam pattern." },
  { n: "02", t: "Attempt in a real interface", d: "Question palette, timer, mark-for-review and autosave — just like the actual CBT." },
  { n: "03", t: "Get a full analysis", d: "Score, accuracy, time-per-question and subject-wise breakdown, instantly." },
  { n: "04", t: "Review and improve", d: "Study solutions, revisit weak areas and track progress across attempts." },
];

export default function Home() {
  const { settings, exams } = useSite();
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({});
  useSeo({ title: `${settings.brand_name || "Agni Shiksha"} — Exam practice, mocks & PYQs for SSC, UPSC, Banking & more`, description: settings.hero_subtext });

  useEffect(() => {
    Promise.all([
      api.get("/products?featured=true&limit=6"),
      api.get("/testimonials"),
      api.get("/faqs?scope=general"),
      api.get("/blog?limit=3"),
      api.get("/products?limit=100"),
    ]).then(([f, t, q, b, all]) => {
      setFeatured(f.data); setTestimonials(t.data); setFaqs(q.data); setPosts(b.data);
      const c = {}; all.data.forEach((p) => { c[p.exam_slug] = (c[p.exam_slug] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
  }, []);

  return (
    <div className="overflow-hidden">
      {/* 01 · HERO */}
      <section className="relative dotgrid">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 55% at 78% 8%, rgba(249,115,22,0.14), transparent 60%)" }} />
        <div className="container-max container-px relative pt-10 pb-16 lg:pt-16 lg:pb-24 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-10 items-center">
          <div>
            <Reveal><p className="eyebrow mb-5">India's exam practice platform</p></Reveal>
            <Reveal delay={60}>
              <h1 className="font-heading text-[34px] leading-[1.06] sm:text-5xl lg:text-[56px] font-extrabold tracking-tight">
                Practice for the exam<br className="hidden sm:block" /> you actually have to crack.
              </h1>
            </Reveal>
            <Reveal delay={120}>
              <p className="text-dim text-[15px] sm:text-lg mt-6 max-w-xl leading-relaxed">
                Full-length mocks, PYQs and MCQ practice built around real exam patterns — with detailed performance analysis after every attempt.
              </p>
            </Reveal>
            <Reveal delay={180}>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button asChild size="lg" className="btn-ember rounded-full h-12 px-6 text-[15px]" data-testid="hero-primary-cta">
                  <Link to="/pricing">Explore Test Series <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 text-[15px] border-line bg-white/5 hover:bg-white/10 hover:text-content" data-testid="hero-secondary-cta">
                  <Link to="/free-tests"><Play className="w-4 h-4 mr-2" /> Try a Free Mock</Link>
                </Button>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-9">
                {CAPABILITIES.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 text-[13px] text-content-secondary">
                    <Check className="w-4 h-4 text-ember" /> {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={200} className="lg:pl-6">
            <div className="float"><TestInterfaceMockup /></div>
          </Reveal>
        </div>
      </section>

      {/* 02 · EXAM DISCOVERY */}
      <section id="exams" className="container-max container-px section-y">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <SectionHeader eyebrow="Choose your exam" title="One platform. Different exam patterns." subtitle="Practice built around the specific test you're preparing for — not a generic question dump." />
          <Link to="/pricing" className="hidden lg:inline-flex items-center gap-1 text-[13px] text-ember shrink-0">All test series <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line rounded-2xl overflow-hidden border border-line">
          {exams.map((e, i) => {
            const Icon = examIcon(e.icon);
            return (
              <Reveal key={e.slug} delay={i * 40}>
                <Link to={`/${e.slug}`} data-testid={`exam-category-card-${e.slug}`}
                  className="group bg-surface hover:bg-card-hover transition-colors p-6 flex flex-col h-full min-h-[168px]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-line flex items-center justify-center group-hover:border-ember/40 transition-colors">
                      <Icon className="w-5 h-5 text-ember" />
                    </div>
                    {counts[e.slug] ? <span className="text-[11px] text-content-muted">{counts[e.slug]} series</span> : null}
                  </div>
                  <h3 className="font-heading font-semibold text-[15px] mb-1">{e.name}</h3>
                  <p className="text-[13px] text-content-secondary leading-snug">{e.short_description}</p>
                  <span className="mt-auto pt-4 inline-flex items-center gap-1 text-[13px] text-content-secondary group-hover:text-ember transition-colors">Explore <ChevronRight className="w-3.5 h-3.5" /></span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* 03 · VALUE PROP band */}
      <section className="container-max container-px pb-4">
        <div className="panel px-6 sm:px-10 py-10 grid sm:grid-cols-3 gap-8">
          {[["Don't just answer.", "See how you perform under real exam conditions."],
            ["Don't just score.", "Understand the accuracy, timing and subjects behind it."],
            ["Don't just study.", "Turn every attempt into a clear plan for the next one."]].map(([a, b], i) => (
            <Reveal key={i} delay={i * 60}>
              <div>
                <div className="font-heading font-bold text-lg mb-1.5">{a}</div>
                <p className="text-[14px] text-content-secondary leading-relaxed">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 04 · WHY (benefits editorial grid) */}
      <section className="container-max container-px section-y">
        <SectionHeader eyebrow="Why Agni Shiksha" title="Preparation that doesn't stop at the right answer."
          subtitle="Realistic mock tests, previous-year questions and performance analysis working together — so you know exactly where you stand before exam day." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line rounded-2xl overflow-hidden border border-line mt-10">
          {VALUES.map((v, i) => (
            <Reveal key={i} delay={i * 40}>
              <div className="bg-surface p-6 lg:p-7 h-full">
                <v.icon className="w-5 h-5 text-ember mb-4" />
                <h3 className="font-heading font-semibold text-[15px] mb-2">{v.title}</h3>
                <p className="text-[13px] text-content-secondary leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 05 · REAL EXAM EXPERIENCE split */}
      <section className="container-max container-px section-y">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <p className="eyebrow mb-4">The test experience</p>
            <h2 className="font-heading text-[26px] sm:text-3xl lg:text-[38px] font-bold leading-[1.12]">Practice should feel familiar before exam day.</h2>
            <p className="text-dim mt-5 text-[15px] leading-relaxed">Every mock runs in an interface modelled on real computer-based tests — so the controls never surprise you when it matters.</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-8">
              {[[ListChecks, "Colour-coded question palette"], [Timer, "Live timer with auto-submit"], [RefreshCw, "Mark for review & clear response"], [Layers, "Section navigation & autosave"]].map(([Icon, t], i) => (
                <div key={i} className="flex items-center gap-3 text-[14px] text-content-secondary"><Icon className="w-4 h-4 text-ember shrink-0" /> {t}</div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}><TestInterfaceMockup /></Reveal>
        </div>
      </section>

      {/* 06 · TEST SERIES */}
      {featured.length > 0 && (
        <section className="container-max container-px section-y">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <SectionHeader eyebrow="Test series" title="Test series built around your exam." subtitle="Clear pricing, a free demo in every series, and detailed analysis after every mock." />
            <Link to="/pricing" className="hidden lg:inline-flex items-center gap-1 text-[13px] text-ember shrink-0">Browse all <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((p, i) => <Reveal key={p.id} delay={i * 50}><ProductCard p={p} /></Reveal>)}
          </div>
        </section>
      )}

      {/* 07 · HOW IT WORKS timeline */}
      <section className="container-max container-px section-y">
        <SectionHeader eyebrow="How it works" title="From your first mock to a clear plan." center />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 60}>
              <div className="relative h-full">
                <div className="font-mono text-ember/70 text-[13px] mb-3">{s.n}</div>
                <h3 className="font-heading font-semibold text-[15px] mb-1.5">{s.t}</h3>
                <p className="text-[13px] text-content-secondary leading-relaxed">{s.d}</p>
                {i < STEPS.length - 1 && <div className="hidden lg:block absolute top-1.5 right-[-14px] w-7 h-px bg-line" />}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 08 · ANALYTICS split (reversed) */}
      <section className="container-max container-px section-y">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal className="order-2 lg:order-1"><AnalyticsMockup /></Reveal>
          <Reveal delay={120} className="order-1 lg:order-2">
            <p className="eyebrow mb-4">Performance analytics</p>
            <h2 className="font-heading text-[26px] sm:text-3xl lg:text-[38px] font-bold leading-[1.12]">Know where you stand before the real exam.</h2>
            <p className="text-dim mt-5 text-[15px] leading-relaxed">Each attempt breaks down into the numbers that actually improve a score — accuracy, time per question, attempt rate and subject-level performance, with your strengths and weak areas surfaced automatically.</p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              {["Accuracy", "Time / question", "Subject performance", "Rank & percentile", "Strengths", "Weak areas"].map((t) => <span key={t} className="chip">{t}</span>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 09 · ECOSYSTEM */}
      <section className="container-max container-px section-y">
        <SectionHeader eyebrow="One connected practice loop" title="Mocks, PYQs and MCQs — in one place." center />
        <div className="grid sm:grid-cols-3 gap-5 mt-12">
          {[["Full-length mocks", "Simulate the complete paper end-to-end, under timed conditions."],
            ["Previous-year questions", "Learn the patterns examiners repeat, organised by exam and subject."],
            ["Focused MCQ practice", "Drill specific subjects and topics between full mocks."]].map(([t, d], i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="panel p-6 h-full">
                <div className="font-mono text-ember/70 text-[13px] mb-3">{`0${i + 1}`}</div>
                <h3 className="font-heading font-semibold text-[16px] mb-2">{t}</h3>
                <p className="text-[13px] text-content-secondary leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 10 · WHO IT'S FOR */}
      <section className="container-max container-px section-y">
        <SectionHeader eyebrow="Built for" title="Made for how India actually prepares." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {AUDIENCE.map((a, i) => (
            <Reveal key={i} delay={i * 50}>
              <div className="border-l-2 border-ember/40 pl-5 h-full">
                <h3 className="font-heading font-semibold text-[15px] mb-1.5">{a.t}</h3>
                <p className="text-[13px] text-content-secondary leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 11 · SOCIAL PROOF (real testimonials only) */}
      {testimonials.length > 0 ? (
        <section className="container-max container-px section-y">
          <SectionHeader eyebrow="Student stories" title="What aspirants say." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {testimonials.slice(0, 3).map((t, i) => (
              <Reveal key={t.id} delay={i * 50}>
                <figure className="panel p-6 h-full flex flex-col">
                  <blockquote className="text-[14px] text-content leading-relaxed flex-1">“{t.text}”</blockquote>
                  <figcaption className="flex items-center gap-3 mt-5 pt-5 border-t border-line">
                    <span className="w-9 h-9 rounded-full bg-white/5 border border-line text-ember flex items-center justify-center font-semibold text-sm">{t.name?.[0]}</span>
                    <span><span className="block text-[13px] font-medium">{t.name}</span><span className="block text-[12px] text-content-muted">{t.exam} · {t.city}</span></span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <p className="text-[11px] text-content-muted mt-4">Demo content — manage or remove from the admin panel.</p>
        </section>
      ) : (
        <section className="container-max container-px section-y">
          <div className="panel p-8 sm:p-12 text-center max-w-3xl mx-auto">
            <p className="eyebrow mb-4">Built for serious preparation</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">We'd rather show you the product than borrowed praise.</h2>
            <p className="text-dim leading-relaxed">Agni Shiksha is designed around one idea: realistic practice with honest feedback. Try a free mock and judge the experience for yourself.</p>
            <Button asChild className="btn-ember rounded-full mt-7 h-11 px-6"><Link to="/free-tests">Try a Free Mock</Link></Button>
          </div>
        </section>
      )}

      {/* 12 · FAQ */}
      {faqs.length > 0 && (
        <section className="container-max container-px section-y">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
            <SectionHeader eyebrow="FAQ" title="Answers, before you ask." subtitle="Everything about products, access and the test experience." />
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border-b border-line">
                  <AccordionTrigger className="text-left text-[15px] font-medium hover:no-underline py-5" data-testid={`faq-${f.id}`}>{f.question}</AccordionTrigger>
                  <AccordionContent className="text-[14px] text-content-secondary leading-relaxed pb-5">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* 13 · BLOG */}
      {posts.length > 0 && (
        <section className="container-max container-px section-y">
          <div className="flex items-end justify-between gap-6 mb-10">
            <SectionHeader eyebrow="Resources" title="Preparation strategies & updates." />
            <Link to="/blog" className="hidden sm:inline-flex items-center gap-1 text-[13px] text-ember shrink-0">All articles <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {posts.map((b, i) => (
              <Reveal key={b.id} delay={i * 50}>
                <Link to={`/blog/${b.slug}`} className="group block h-full border-t border-line pt-5">
                  <div className="flex items-center gap-3 text-[11px] text-content-muted mb-3">
                    <span className="text-ember uppercase tracking-wider">{b.category}</span>
                    <span>·</span><span>4 min read</span>
                  </div>
                  <h3 className="font-heading font-semibold text-[16px] leading-snug mb-2 group-hover:text-ember transition-colors">{b.title}</h3>
                  <p className="text-[13px] text-content-secondary leading-relaxed line-clamp-2">{b.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* 14 · FINAL CTA */}
      <section className="container-max container-px pb-24">
        <div className="relative panel overflow-hidden px-6 sm:px-12 py-14 sm:py-16 text-center">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(249,115,22,0.16), transparent 65%)" }} />
          <div className="relative">
            <h2 className="font-heading text-[28px] sm:text-4xl font-bold leading-tight max-w-2xl mx-auto">Start with a free mock. Decide with your own score.</h2>
            <p className="text-dim mt-4 max-w-xl mx-auto text-[15px]">No credit card to try. Experience the real interface and full analysis in minutes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button asChild size="lg" className="btn-ember rounded-full h-12 px-7"><Link to="/free-tests">Try a Free Mock</Link></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-line bg-white/5 hover:bg-white/10 hover:text-content"><Link to="/pricing">Explore Test Series</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
